"use client";

import * as React from "react";
import { LazyMotion, domAnimation, m } from "framer-motion";
import type { MotionProps } from "framer-motion";
import type { ButtonProps } from "@relume_io/relume-ui";
import CtaButton from "./ui/CtaButton";
import Breadcrumbs, { type BreadcrumbItem } from "./Breadcrumbs";
import Underline from "./ui/underline";
import clsx from "clsx";

type Props = {
    breadcrumbs?: BreadcrumbItem[];
    tagline?: string;
    heading: string;
    description: string;
    button?: ButtonProps;
};

type ConflictingHtmlEvents =
    | "onAnimationStart"
    | "onAnimationEnd"
    | "onAnimationIteration"
    | "onDrag"
    | "onDragStart"
    | "onDragEnd"
    | "onDragEnter"
    | "onDragLeave"
    | "onDragOver"
    | "onDrop";

type SafeSectionHtmlProps = Omit<
    React.ComponentPropsWithoutRef<"section">,
    keyof Props | keyof MotionProps | ConflictingHtmlEvents
>;

export type HeroAllServicesMotionProps = Props & SafeSectionHtmlProps & MotionProps;

const slideUp = {
    hidden: { y: 20, opacity: 0 },
    visible: (i: number) => ({
        y: 0,
        opacity: 1,
        transition: {
            duration: 0.6,
            ease: [0.215, 0.61, 0.355, 1],
            delay: i * 0.1,
        },
    }),
} as const;

export default function HeroAllServicesMotion({
                                                  breadcrumbs,
                                                  tagline,
                                                  heading,
                                                  description,
                                                  button,
                                                  className,
                                                  ...rest
                                              }: HeroAllServicesMotionProps) {
    return (
        <LazyMotion features={domAnimation}>
            <m.section
                id="relume"
                initial="hidden"
                animate="visible"
                className={clsx("px-[5%] py-16 md:py-24 lg:py-28 mt-24 md:mt-28", className)}
                {...rest}
            >
                <div className="container">
                    <div className="mx-auto max-w-7xl">
                        {/* ✅ Tablet: stack / ✅ Desktop: 12 kolumn */}
                        <div className="grid items-start gap-3 lg:grid-cols-12 lg:gap-6 xl:gap-8">
                            <div className="lg:col-span-2 -mt-10 md:-mt-14 mb-4 md:mb-6">
                                {(breadcrumbs && breadcrumbs.length > 0) ? (
                                    <m.div
                                        variants={slideUp}
                                        custom={0}
                                        className="relative inline-block will-change-transform"
                                    >
                                        <Breadcrumbs items={breadcrumbs} />
                                        <Underline
                                            delay={0.6}
                                            className="pointer-events-none absolute -left-6 md:-left-8 top-full mt-2 text-accent h-5 w-[170px] md:h-6 md:w-[230px]"
                                        />
                                    </m.div>
                                ) : tagline ? (
                                    <m.div
                                        variants={slideUp}
                                        custom={0}
                                        className="relative inline-block will-change-transform"
                                    >
                                        <p className="text-[11px] uppercase tracking-[0.08em]">{tagline}</p>
                                        <Underline
                                            delay={0.6}
                                            className="pointer-events-none absolute -left-6 md:-left-8 top-full mt-2 text-accent h-5 w-[170px] md:h-6 md:w-[230px]"
                                        />
                                    </m.div>
                                ) : null}
                            </div>

                            {/* ✅ Tablet: pełna szerokość */}
                            <div className="lg:col-span-8">
                                <m.h1
                                    variants={slideUp}
                                    custom={1}
                                    className="font-medium text-5xl md:text-7xl will-change-transform"
                                >
                                    {heading}
                                </m.h1>
                            </div>
                        </div>

                        {/* ✅ Tablet: opis POD nagłówkiem / ✅ Desktop: opis po prawej */}
                        <div className="mt-6 md:mt-10 lg:mt-24 grid lg:grid-cols-12">
                            <div className="lg:col-start-7 lg:col-span-6">
                                {description && (
                                    <m.p
                                        variants={slideUp}
                                        custom={2}
                                        className="md:text-md will-change-transform max-w-[60ch] lg:max-w-none"
                                    >
                                        {description}
                                    </m.p>
                                )}

                                {button && (
                                    <m.div variants={slideUp} custom={3} className="mt-6 md:mt-8 will-change-transform">
                                        <CtaButton />
                                    </m.div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </m.section>
        </LazyMotion>
    );
}